import type { BtlApplication } from 'api/btlApplicationForm/types';
import { PropertyTenure } from 'api/btlApplicationForm/types';
import { firmApi } from 'api/firm/firm.api';
import type { ExistingFirmResponse } from 'api/firm/firm.types';
import { isStrideUpInternalFirm } from 'utils/strideUpInternalFirm';
import { monthsToYears } from 'utils/numbers';
import { mapIntroducerFormValuesFromCaseReferrer } from './btlIntroducerHelpers';
import { resolveBtlInitialFunnelTypeFromApi } from './btlSubmissionRouteHelpers';
import { resolveInitialFunnelTypeFromApi } from 'pages/ApplicationFormPage/Submission/submissionRouteHelpers';
import { mapDepositSourcesToFormValues } from './depositSourcesHelpers';

export function mapApplicationToFormValues(data?: BtlApplication, firm?: ExistingFirmResponse | null) {
  if (!data) {
    const submissionFunnelType = firm ? resolveBtlInitialFunnelTypeFromApi(firm, undefined) : undefined;

    return {
      purpose: 'PURCHASE',
      feesAddedToBalance: false,
      ...(submissionFunnelType ? { submissionFunnelType } : {}),
    };
  }

  const submissionFunnelType = firm
    ? resolveBtlInitialFunnelTypeFromApi(firm, data.submissionRoute?.submissionFunnelType)
    : undefined;

  const { applicationProperty, termInMonths, feesAddedToBalance, purpose, depositSources, ...rest } = data;

  if (purpose === 'PURCHASE') {
    rest.refinancePurpose = null;
  }

  const mappedDepositSources = mapDepositSourcesToFormValues(depositSources);

  const { tenure, yearsRemainingOnLease, estateAgent } = applicationProperty ?? {};
  const normalisedTenure: PropertyTenure | null = tenure === 'LEASEHOLD' || tenure === 'FREEHOLD' ? tenure : null;

  const sharedValues = {
    ...rest,
    purpose,
    ...(submissionFunnelType ? { submissionFunnelType } : {}),
    feesAddedToBalance: [feesAddedToBalance],
    estateAgent,
    termInMonths: termInMonths ? monthsToYears(termInMonths) : undefined,
    tenure: normalisedTenure,
    yearsRemainingOnLease,
    depositSources: mappedDepositSources,
  };

  if (applicationProperty?.status === 'REJECTED') {
    return sharedValues;
  }

  const {
    purchasePrice,
    grossMonthlyRentalPayment,
    propertyType,
    propertySubType,
    expectedPropertyType,
    address,
    attributes,
  } = applicationProperty?.property ?? {};
  const { numberOfBedrooms, hasCommunalSpace, numberOfUnits } = attributes ?? {};

  return {
    ...sharedValues,
    purchasePrice,
    grossMonthlyRentalPayment,
    propertyType,
    propertySubType,
    expectedPropertyType,
    address: { ...address, isManual: true },
    numberOfBedrooms,
    hasCommunalSpace: [hasCommunalSpace],
    numberOfUnits,
  };
}

export async function hydrateFinancingAndPropertyFormValues(
  data?: BtlApplication,
  firm?: ExistingFirmResponse | null,
  brokerReferrerId?: string,
): Promise<Record<string, unknown>> {
  const baseValues = mapApplicationToFormValues(data, firm);

  if (!data || !firm || !isStrideUpInternalFirm(firm) || !brokerReferrerId) {
    return baseValues;
  }

  const caseReferrerId = data.submissionRoute?.referrerId;
  let introducerReferrer = null;

  if (caseReferrerId && caseReferrerId !== brokerReferrerId) {
    try {
      introducerReferrer = await firmApi.getIntroducerById(caseReferrerId);
    } catch {
      introducerReferrer = null;
    }
  }

  const introducerValues = mapIntroducerFormValuesFromCaseReferrer(
    caseReferrerId,
    brokerReferrerId,
    introducerReferrer,
  );

  if (!introducerValues) {
    return baseValues;
  }

  return {
    ...baseValues,
    ...introducerValues,
  };
}

export async function hydrateOoFinancingFormValues(
  data?: BtlApplication,
  firm?: ExistingFirmResponse | null,
  brokerReferrerId?: string,
): Promise<Record<string, unknown>> {
  const baseValues = await hydrateFinancingAndPropertyFormValues(data, firm, brokerReferrerId);
  const intermediaryFees = data?.submissionRoute?.intermediaryFees ?? [];
  const submissionFunnelType = firm
    ? resolveInitialFunnelTypeFromApi(firm, data?.submissionRoute?.submissionFunnelType)
    : baseValues.submissionFunnelType;

  return {
    ...baseValues,
    ...(submissionFunnelType ? { submissionFunnelType } : {}),
    hasIntermediaryFees: intermediaryFees.length > 0 ? 'true' : 'false',
    fees: intermediaryFees,
    region: data?.applicationProperty?.property?.region,
  };
}
